/**
 * CandleCardPage — `/candle-card`
 * -------------------------------
 * The client's health card: what they hold, what it has collected, and what
 * each of their analyses found.
 *
 * Shape of the page, and why:
 *
 *   the card        the hero is the object itself, not a statistic. A card
 *                   section that opens with a number and a label could be any
 *                   dashboard; a card that holds its proportions reads as
 *                   something you own.
 *   coverage        the reason to come back. It counts analyses COLLECTED —
 *                   something the person did — never a score about their face.
 *   readings        one tile per report, each speaking its own bot's language.
 *   plans           last, because tiers only make sense once you can see what
 *                   you already have.
 *
 * Nothing here reads an analysis bot's output directly: every figure comes
 * through `features/candleCard/reportAdapters.js`, so a bot can change its
 * summary without this page changing at all.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, Skeleton, Snackbar, Stack, Typography,
} from '@mui/material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import Container from '@/components/PageSheet';
import DiscountField from '@/components/DiscountField';
import CandleCardHero from '@/features/candleCard/CandleCardHero';
import CoverageRow from '@/features/candleCard/CoverageRow';
import ReadingTile from '@/features/candleCard/ReadingTile';
import { familyOf } from '@/features/candleCard/reportAdapters';
import { candleCardApi, cardsApi } from '@/services/api';
import { localized } from '@/utils/localizedContent';
import { useAuth } from '@/hooks/useAuth';

/** Tiers weakest-first, so "what you could move up to" is just what follows. */
const TIER_ORDER = ['free', 'pro', 'special_care'];

export default function CandleCardPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith('fa') ? 'fa' : 'en';
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();

  const [buying, setBuying] = useState(null);
  const [discountCode, setDiscountCode] = useState(null);
  const [toast, setToast] = useState('');

  // Cleared with the dialog: a code typed against one plan must not quietly
  // ride along to another, where its scope or minimum may not hold.
  const closeBuy = () => { setBuying(null); setDiscountCode(null); };

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['candle-card'],
    queryFn: () => candleCardApi.getCandleCard(),
  });

  const buyMut = useMutation({
    mutationFn: ({ code, gateway }) => cardsApi.buy(code, gateway, discountCode),
    onSuccess: (res) => {
      if (res?.paymentUrl) { window.location.assign(res.paymentUrl); return; }
      closeBuy();
      setToast(t('candleCard.bought'));
      qc.invalidateQueries({ queryKey: ['candle-card'] });
      qc.invalidateQueries({ queryKey: ['my-cards'] });
    },
    onError: (err) =>
      setToast(err?.response?.data?.error?.message || t('common.error')),
  });

  const tier = data?.tier || 'free';
  const plans = data?.plans || [];
  // Memoised off `data` rather than a fresh `|| []`, which would be a new
  // array identity on every render and defeat the memo below it.
  const reports = useMemo(() => data?.reports || [], [data]);

  const haveFamilies = useMemo(
    () => new Set(reports.map((r) => familyOf(r.agentKey)).filter(Boolean)),
    [reports],
  );

  const formatDate = (iso) => new Date(iso).toLocaleDateString(
    lang === 'fa' ? 'fa-IR' : 'en-GB', { year: 'numeric', month: 'long' });

  const money = (v) => t('card.priceToman', {
    amount: new Intl.NumberFormat(lang === 'fa' ? 'fa-IR' : 'en-GB').format(v || 0),
  });

  const holder = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim();

  if (isLoading) {
    return (
      <Container maxWidth="sm" sx={{ py: { xs: 2, sm: 4 } }}>
        <Skeleton variant="rounded" sx={{ aspectRatio: '1.586 / 1', maxWidth: 420,
          mx: 'auto', borderRadius: 4 }} />
        <Skeleton variant="rounded" height={110} sx={{ mt: 3, borderRadius: 3 }} />
        <Skeleton variant="rounded" height={160} sx={{ mt: 2, borderRadius: 3 }} />
      </Container>
    );
  }

  if (isError) {
    return (
      <Container maxWidth="sm" sx={{ py: { xs: 2, sm: 4 } }}>
        <Alert severity="error" variant="outlined" sx={{ borderRadius: 3 }}
          action={<Button size="small" onClick={() => refetch()}>{t('common.retry')}</Button>}>
          {t('candleCard.loadError')}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 2, sm: 4 } }}>
      <Stack spacing={3.5}>
        <Box>
          <CandleCardHero
            tier={tier}
            holderName={holder}
            keptUntil={data?.photosKeptUntil}
            tierLabel={t(`candleCard.tier.${tier}`)}
            validLabel={t('candleCard.keptUntil')}
            noCoverLabel={t('candleCard.noCover')}
            formatDate={formatDate}
          />
          <Typography variant="body2"
            sx={{ mt: 1.5, color: 'text.secondary', textAlign: 'center' }}>
            {t('candleCard.cardCaption')}
          </Typography>
        </Box>

        <CoverageRow
          haveFamilies={haveFamilies}
          t={t}
          lang={lang}
          onPick={() => navigate('/services')}
        />

        <Box>
          <Typography variant="subtitle1" fontWeight={700} component="h2" sx={{ mb: 1.25 }}>
            {t('candleCard.readings.title')}
          </Typography>

          {reports.length === 0 ? (
            <Box sx={{ p: 3, borderRadius: 3, border: '1px dashed', borderColor: 'divider',
              textAlign: 'center' }}>
              <Typography fontWeight={700}>{t('candleCard.readings.emptyTitle')}</Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mt: .5, mb: 2 }}>
                {t('candleCard.readings.emptyBody')}
              </Typography>
              <Button variant="contained" onClick={() => navigate('/services')}>
                {t('candleCard.readings.emptyCta')}
              </Button>
            </Box>
          ) : (
            <Stack spacing={1.5}>
              {reports.map((r) => (
                <ReadingTile
                  key={r.consultationId}
                  report={{ ...r, serviceName: localized(r.serviceName, lang) }}
                  t={t}
                  lang={lang}
                  // A reading opens as pictures of the client's own face.
                  // The consultation page is still where the doctor's words
                  // and the chat live; this is the report itself.
                  onOpen={() => navigate(`/candle-card/report/${encodeURIComponent(r.consultationId)}`)}
                />
              ))}
            </Stack>
          )}
        </Box>

        <Box>
          <Typography variant="subtitle1" fontWeight={700} component="h2" sx={{ mb: .5 }}>
            {t('candleCard.plans.title')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
            {t('candleCard.plans.subtitle')}
          </Typography>

          <Stack spacing={1.5}>
            {plans.map((p) => {
              const isCurrent = p.tier === tier;
              const isUpgrade = TIER_ORDER.indexOf(p.tier) > TIER_ORDER.indexOf(tier);
              return (
                <Box key={p.code} sx={{
                  p: 2, borderRadius: 3, border: '1px solid',
                  borderColor: isCurrent ? 'rgba(192,167,134,.9)' : 'divider',
                  bgcolor: isCurrent ? 'rgba(192,167,134,.14)' : '#FFFFFF',
                }}>
                  <Stack direction="row" justifyContent="space-between"
                    alignItems="flex-start" spacing={1.5}>
                    <Box sx={{ minWidth: 0 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography fontWeight={700}>{localized(p.name, lang)}</Typography>
                        {isCurrent && (
                          <Chip size="small" label={t('candleCard.plans.current')}
                            sx={{ bgcolor: '#C0A786', color: '#11223E', fontWeight: 700 }} />
                        )}
                      </Stack>
                      <Typography variant="body2" sx={{ color: 'text.secondary', mt: .5 }}>
                        {localized(p.description, lang)}
                      </Typography>
                      {(p.features || []).length > 0 && (
                        <Stack sx={{ mt: 1 }} spacing={.5}>
                          {p.features.map((f) => (
                            <Stack key={f} direction="row" spacing={.75} alignItems="center">
                              <CheckRoundedIcon sx={{ fontSize: 15, color: '#2E7D55' }} />
                              <Typography variant="caption">
                                {t(`candleCard.feature.${f}`, { defaultValue: f })}
                              </Typography>
                            </Stack>
                          ))}
                        </Stack>
                      )}
                    </Box>
                    <Box sx={{ textAlign: 'end', flexShrink: 0 }}>
                      <Typography fontWeight={700} sx={{ whiteSpace: 'nowrap' }}>
                        {money(p.priceToman)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {/* `count` picks the plural form; `n` is the same
                            number formatted for the reader's language, since
                            i18next interpolates `count` as raw Latin digits. */}
                        {(() => {
                          const months = Math.round((p.retentionDays || 0) / 30);
                          return t('candleCard.plans.months', {
                            count: months,
                            n: new Intl.NumberFormat(lang === 'fa' ? 'fa-IR' : 'en-GB')
                              .format(months),
                          });
                        })()}
                      </Typography>
                    </Box>
                  </Stack>
                  {!isCurrent && (
                    <Button
                      fullWidth variant={isUpgrade ? 'contained' : 'outlined'}
                      sx={{ mt: 1.5 }}
                      onClick={() => { setDiscountCode(null); setBuying(p); }}
                    >
                      {isUpgrade ? t('candleCard.plans.upgrade') : t('candleCard.plans.choose')}
                    </Button>
                  )}
                </Box>
              );
            })}
          </Stack>
        </Box>
      </Stack>

      <Dialog open={Boolean(buying)} onClose={closeBuy} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 800 }}>
          {buying ? localized(buying.name, lang) : ''}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {buying ? localized(buying.description, lang) : ''}
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Stack direction="row" justifyContent="space-between" alignItems="baseline">
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {t('candleCard.plans.price')}
            </Typography>
            <Typography fontWeight={800}>{money(buying?.priceToman)}</Typography>
          </Stack>
          {buying && (
            <DiscountField
              amountToman={buying.priceToman}
              context="card"
              onChange={setDiscountCode}
              t={t}
              lang={lang}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeBuy} disabled={buyMut.isPending}>{t('common.cancel')}</Button>
          <Button variant="contained" disabled={buyMut.isPending}
            onClick={() => buyMut.mutate({ code: buying.code })}>
            {buyMut.isPending ? t('common.loading') : t('candleCard.plans.pay')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={Boolean(toast)} autoHideDuration={4000}
        onClose={() => setToast('')} message={toast} />
    </Container>
  );
}
